/* eslint-disable no-unused-vars */
import {
  Box,
  CircularProgress,
  IconButton,
  Popper,
  PopperProps,
  TextField,
  TextFieldProps,
} from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
  Autocomplete,
  createFilterOptions,
  FilterOptionsState,
} from '@material-ui/lab';
import { untree, getLevel, getAllParentsByChildren } from 'helpers/untree';
import React from 'react';
import ListboxComponent from 'components/atoms/ListboxComponent';
import useStyles from './style';
import {
  IAutocompleteTreeProps,
  IAutocompleteTreeRef,
  IOption,
  IOptionId,
} from './types';

const AutocompleteTree = React.forwardRef<
  IAutocompleteTreeRef,
  IAutocompleteTreeProps
>(
  (
    {
      id,
      items,
      label,
      name,
      className,
      placeholder,
      disabled,
      initialState = null,
      disableClearable,
      loading,
      noOptionsText,
      defaultExpanded = [],
      extraTextFieldProps = {},
      onChange,
      defaultValue,
    },
    ref,
  ) => {
    const classes = useStyles();
    const scrollRef = React.useRef<number | null>(0);

    const [expanded, setExpanded] = React.useState<IOptionId[]>([]);
    const [inputValue, setInputValue] = React.useState<string>('');
    const [autocompleteInputValue, setAutocompleteInputValue] =
      React.useState<string>('');
    const [selectedOption, setSelectedOption] = React.useState<IOption | null>(
      () => initialState,
    );

    const disableExpandIconButton = React.useMemo(() => {
      return Boolean(inputValue.length);
    }, [inputValue.length]);

    const MyPopper = React.useCallback(
      (popperProps: PopperProps) => (
        <Popper
          {...popperProps}
          style={{
            minWidth: '500px',
            width: 'fit-content',
            maxWidth: '800px',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
          placement="bottom-start"
        />
      ),
      [],
    );

    const inputRenderer = React.useCallback(
      (params: TextFieldProps) => {
        const combinedProps = {
          ...params,
          ...extraTextFieldProps,
        } as TextFieldProps;

        return (
          <TextField
            variant="outlined"
            {...combinedProps}
            label={label}
            placeholder={placeholder}
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress
                      aria-label="autocomplete-loading"
                      color="inherit"
                      size={25}
                    />
                  ) : null}
                  {params?.InputProps?.endAdornment}
                </>
              ),
            }}
          />
        );
      },
      [extraTextFieldProps, label, placeholder, loading],
    );

    const getItemLevel = React.useCallback(
      (option: IOption) => {
        return getLevel(option, items, 0);
      },
      [items],
    );

    const expandAll = React.useMemo(() => {
      return Boolean(inputValue.length);
    }, [inputValue.length]);

    const isExpanded = React.useCallback(
      (optionId: IOptionId) => {
        return expandAll || expanded.includes(optionId);
      },
      [expandAll, expanded],
    );

    const getRecursivelyExpandedItems = React.useCallback(
      (itemTree: IOption[]): IOption[] => {
        const extraCondition = (item: IOption) => isExpanded(item.id);
        return untree(itemTree, extraCondition);
      },
      [isExpanded],
    );

    const recursiveItems = React.useMemo(() => {
      return getRecursivelyExpandedItems(items);
    }, [getRecursivelyExpandedItems, items]);

    const getOptionDisabled = React.useCallback((option: IOption) => {
      return Boolean(option?.disabled);
    }, []);

    const getOptionLabel = React.useCallback((option: IOption) => {
      return option.description;
    }, []);

    const getOptionSelected = React.useCallback(
      (option: IOption, value: IOption) => {
        return option.id === value.id;
      },
      [],
    );

    const handleToggleOption = React.useCallback(
      (toggleOption: IOption | null) => setSelectedOption(toggleOption),
      [],
    );

    const handleInputChange = React.useCallback(
      (
        event: React.ChangeEvent<Record<string, unknown>>,
        newInputValue: string,
        reason: string,
      ) => {
        setAutocompleteInputValue(newInputValue);
        setInputValue(
          ['clear', 'reset'].some(r => r === reason) ? '' : newInputValue,
        );
      },
      [],
    );

    const handleClearOptions = React.useCallback(
      () => setSelectedOption(null),
      [],
    );

    const handleChange = React.useCallback(
      (
        event: React.ChangeEvent<Record<string, unknown>>,
        currentOption: IOption | null,
        reason: string,
      ) => {
        if (reason === 'select-option' || reason === 'remove-option') {
          handleToggleOption(currentOption);
        } else if (reason === 'clear') {
          handleClearOptions();
        }

        onChange(name, currentOption);
      },
      [onChange, name, handleToggleOption, handleClearOptions],
    );

    const handleToggleTree = React.useCallback(
      // eslint-disable-next-line @typescript-eslint/ban-types
      (event: React.MouseEvent<{}>, nodeId: IOptionId) => {
        event.preventDefault();
        event.stopPropagation();

        if (isExpanded(nodeId)) {
          setExpanded(currentValues =>
            currentValues.filter(item => item !== nodeId),
          );
        } else {
          setExpanded(currentValues =>
            Array.from(new Set([...currentValues, nodeId])),
          );
        }
      },
      [isExpanded],
    );

    const filter = createFilterOptions<IOption>({
      matchFrom: 'any',
      limit: 50,
      trim: true,
    });

    const renderFilteredOptions = React.useCallback(
      (options: IOption[], params: FilterOptionsState<IOption>) => {
        const filtered = params.inputValue ? filter(options, params) : options;

        return filtered;
      },
      [filter],
    );

    const optionRenderer = React.useCallback(
      (option: IOption) => {
        const TreeIcon = isExpanded(option.id)
          ? ExpandMoreIcon
          : ChevronRightIcon;

        const itemLevel = getItemLevel(option);
        const canShowIcon = (option?.children || []).length > 0;

        const spacingBefore = 8 * itemLevel;

        return (
          <Box
            aria-label={`${option.id}`}
            component="li"
            display="flex"
            alignItems="center"
          >
            {canShowIcon ? (
              <IconButton
                disabled={disableExpandIconButton}
                style={{
                  marginLeft: spacingBefore,
                }}
                onClick={event => handleToggleTree(event, option.id)}
              >
                <TreeIcon />
              </IconButton>
            ) : (
              <div style={{ width: `${48 + spacingBefore}px` }} />
            )}
            {getOptionLabel(option)}
          </Box>
        );
      },
      [
        disableExpandIconButton,
        getItemLevel,
        getOptionLabel,
        handleToggleTree,
        isExpanded,
      ],
    );

    const onReset = React.useCallback(() => {
      setSelectedOption(null);
      setInputValue('');
      setAutocompleteInputValue('');
      setExpanded([]);
      scrollRef.current = 0;
    }, []);

    React.useImperativeHandle(ref, () => ({
      onReset,
    }));

    React.useEffect(() => {
      if (defaultExpanded.length) {
        setExpanded(currentValue =>
          Array.from(new Set([...currentValue, ...defaultExpanded])),
        );
      }
    }, [defaultExpanded]);

    React.useEffect(() => {
      /**
       * Caso ocorra alteração nos items,
       * a refernencia da posição da lista
       * é resetada.
       */
      scrollRef.current = 0;
    }, [items]);

    React.useEffect(() => {
      if (!selectedOption && defaultValue && items.length) {
        setSelectedOption(defaultValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue, items]);

    // Se tem algo selecionado, sempre vai expandir todos os pais do item selecionado
    React.useEffect(() => {
      if (selectedOption) {
        const allParents = getAllParentsByChildren(selectedOption, items);

        if (allParents) {
          const ids = allParents.map(item => item.id);

          setExpanded(ids);
        }
      }
    }, [items, selectedOption]);

    // Move a lista até o item selecionado ou reseta a posição dela
    React.useEffect(() => {
      if (selectedOption) {
        const indexOfSelectedItem = recursiveItems.findIndex(
          option => option.id === selectedOption.id,
        );
        if (indexOfSelectedItem > 0) {
          scrollRef.current = indexOfSelectedItem;
        }
      } else {
        scrollRef.current = 0;
      }
    }, [items, recursiveItems, selectedOption]);

    return (
      <Autocomplete
        id={id}
        disabled={disabled}
        className={[classes.root, className].filter(c => Boolean(c)).join(', ')}
        options={recursiveItems}
        disableListWrap
        value={selectedOption}
        noOptionsText={noOptionsText}
        disableClearable={disableClearable}
        inputValue={autocompleteInputValue}
        onInputChange={handleInputChange}
        PopperComponent={MyPopper}
        onChange={handleChange}
        ListboxProps={{
          style: { overflowY: 'hidden' },
          scrollRef,
          scrollMode: 'ROW',
        }}
        ListboxComponent={ListboxComponent}
        getOptionDisabled={getOptionDisabled}
        getOptionLabel={getOptionLabel}
        getOptionSelected={getOptionSelected}
        filterOptions={renderFilteredOptions}
        renderOption={optionRenderer}
        renderInput={inputRenderer}
        defaultValue={defaultValue}
      />
    );
  },
);

export default React.memo(AutocompleteTree);
